import React from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { Alert, Checkbox } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import styles from "./OrganizationRoles.module.scss";
import { EXTERNAL_ACCOUNTS_ERROR } from "./RoleFormUtils";

type SelectableAccount = Account & { selected?: boolean };

interface RoleExternalAccountsFieldProps {
  /** Set by `ContextForm.Field`: the external accounts, and the form's change handler and error. */
  field?: string;
  value?: SelectableAccount[];
  onChange?: (accounts: SelectableAccount[]) => void;
  error?: unknown;
}

/**
 * The external accounts a role can send to. Shown only while some account can make external
 * transfers, and inside a `ContextForm.Field` that requires at least one to be selected.
 */
export default function RoleExternalAccountsField({
  value = [],
  onChange = () => {},
  error,
}: RoleExternalAccountsFieldProps) {
  const { l10n } = useLocalization();

  const toggle = (toggled: SelectableAccount) =>
    onChange(
      value.map((account) => {
        const copy = new Account(account) as SelectableAccount;
        copy.selected =
          String(account.id) === String(toggled.id)
            ? !account.selected
            : account.selected;
        return copy;
      })
    );

  const hasError = error === EXTERNAL_ACCOUNTS_ERROR.NONE_SELECTED;

  return (
    <div>
      <p className="nds-subheader">
        {l10n.getString("org-role-external-accounts-title")}
      </p>
      <p className={styles.rowNote}>
        {l10n.getString("org-role-external-accounts-description")}
      </p>
      <div className={cc({ [styles.accountsError]: hasError })}>
        <Alert kind="error" isActive={hasError} isDismissable={false}>
          {l10n.getString("org-role-error-no-external-accounts")}
        </Alert>
      </div>
      {value.map((account) => (
        <div className={styles.nestedCheckRow} key={account.id}>
          <Checkbox
            label={account.getShortDescription()}
            checked={Boolean(account.selected)}
            onChange={() => toggle(account)}
          />
        </div>
      ))}
    </div>
  );
}
